import { FC, useCallback, useState } from "react";
import { toast } from "react-toastify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import { FormikProvider, useFormik, FormikConfig } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { ClaimLinkOutput } from "apiIntegrations/campaigns/ChrisLostHisItems.interface";
import { claim } from "validators/client/chrisLostHisItems";

import { INFO_BOX_TYPE } from "components/InfoBox/types";
import InfoBox from "components/InfoBox";
import CustomButton from "components/CustomButton";
import CustomOutlinedInput from "components/CustomOutlinedInput";
import Recaptcha from "components/Recaptcha";

import { STORAGE } from "enums/storage";

import { API_ROUTES } from "config/routes";

import { getObjectItem, setObjectItem } from "utils/storage";

import fetcher from "lib/fetchJson";

interface LostItemClaim {
  email: string;
}

const initialState: LostItemClaim = {
  email: "",
};

interface SectionChrisLostHisItemsProps {
  canClaim?: boolean;
  contractAddress: string;
  tokenId: number;
  hash: string;
  description?: string;
  privacy?: string;
  emailPrompt?: string;
  buttonText?: string;
  successMessage?: string;
}

const SectionChrisLostHisItems: FC<SectionChrisLostHisItemsProps> = ({
  canClaim = true,
  contractAddress,
  tokenId,
  hash,
  description,
  privacy,
  emailPrompt,
  buttonText,
  successMessage,
}) => {
  const [isClaimed, setIsClaimed] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const storageData = getObjectItem<LostItemClaim>(STORAGE.LOST_ITEM_FORM_DATA);
  const onSubmit: FormikConfig<LostItemClaim>["onSubmit"] = useCallback(
    async (values) => {
      try {
        if (!executeRecaptcha) {
          return Promise.reject({
            message: "Execute recaptcha not yet available",
          });
        }
        const captchaToken = await executeRecaptcha("CHRIS_ITEM_FOUND");
        setObjectItem(STORAGE.LOST_ITEM_FORM_DATA, values);
        const { data } = await fetcher<{ data: ClaimLinkOutput }>(
          API_ROUTES.CHRIS_LOST_HIS_ITEMS,
          {
            ...values,
            contractAddress,
            tokenId,
            hash,
            captchaToken,
          },
          "POST",
        );
        setIsClaimed(!!data.success);
      } catch (e) {
        toast.error(e.data.message ?? "Something went wrong.");
      }
    },
    [executeRecaptcha, contractAddress, tokenId, hash],
  );

  const formik = useFormik({
    initialValues: storageData ?? initialState,
    validationSchema: toFormikValidationSchema(claim),
    validateOnChange: false,
    onSubmit: onSubmit,
  });

  if (!isClaimed) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 5,
          alignItems: "center",
        }}
      >
        <Box width="100%">
          <Typography paragraph>
            {description ||
              "Congratulations! You've found one of Chris' items!"}
          </Typography>
          <Typography>
            {privacy ||
              "By participating in this campaign, you consent to our Privacy Policy and agree to receive marketing communications from us."}
          </Typography>
          <FormikProvider value={formik}>
            <Box component="form" onSubmit={formik.handleSubmit}>
              <CustomOutlinedInput
                label={
                  emailPrompt ||
                  "Type in your e-mail address to confirm you've found the item!"
                }
                labelProps={{
                  fontWeight: 600,
                }}
                inputProps={{
                  id: "email",
                  placeholder: "Email",
                  value: formik.values.email,
                  onChange: formik.handleChange,
                }}
                formControlProps={{
                  fullWidth: true,
                  margin: "normal",
                }}
                error={formik.errors.email}
              />

              <Recaptcha boxProps={{ mb: 2 }} />

              <CustomButton
                sx={{
                  textTransform: "uppercase",
                  ml: "auto",
                  display: "block",
                }}
                type="submit"
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting || !canClaim}
              >
                {buttonText || "Confirm"}
              </CustomButton>
            </Box>
          </FormikProvider>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <InfoBox type={INFO_BOX_TYPE.SUCCESS}>
        {successMessage ||
          "Congratulations! You've successfully confirmed that you've found the item!"}
      </InfoBox>
    </Box>
  );
};

export default SectionChrisLostHisItems;
